import { AnimatePresence, motion } from 'motion/react';
import React, { Children, type ReactNode, createContext, useContext, useEffect, useState } from 'react';
import styled from 'styled-components';

import type { HeaderBorderMode } from '~/app/Sitemap';

// Styled component needed to override styled-components button styles that Tailwind can't reach
const ActionsInner = styled(motion.div)`
  @container header (max-width: 600px) {
    & > div > div {
      max-width: none;
      width: 100%;
    }

    button {
      width: 100%;
    }

    [data-slot='button-group'] {
      width: 100%;
    }
  }
`;

type ActionsEntry = { key: number; content: ReactNode; headerDecoration?: ReactNode };
type SetActions = (entry: ActionsEntry | null) => void;

const SetActionsContext = createContext<SetActions | null>(null);

const HeaderBorderContext = createContext<HeaderBorderMode>(true);
const SetHeaderBorderContext = createContext<((mode: HeaderBorderMode) => void) | null>(null);

const STAGGER_DELAY = 0.04;
const EASE = [0.25, 0.1, 0.25, 1] as const;

export const PageFrameProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [actions, setActions] = useState<ActionsEntry | null>(null);
  const [headerBorder, setHeaderBorder] = useState<HeaderBorderMode>(true);

  return (
    <SetActionsContext.Provider value={setActions}>
      <SetHeaderBorderContext.Provider value={setHeaderBorder}>
        <HeaderBorderContext.Provider value={headerBorder}>
          <ActionsRendererContext.Provider value={actions}>{children}</ActionsRendererContext.Provider>
        </HeaderBorderContext.Provider>
      </SetHeaderBorderContext.Provider>
    </SetActionsContext.Provider>
  );
};

const ActionsRendererContext = createContext<ActionsEntry | null>(null);

export const usePageFrameHeaderDecoration = () => {
  const entry = useContext(ActionsRendererContext);
  return entry?.headerDecoration ? { key: entry.key, content: entry.headerDecoration } : null;
};

export const PageFrameActionsSlot: React.FC = () => {
  const entry = useContext(ActionsRendererContext);
  // Three or more buttons crop their labels at the default 360px stack
  // breakpoint, so bump the threshold to 485px when there are 3+ actions.
  const itemCount = entry ? Children.count(entry.content) : 0;
  const stackClasses =
    itemCount >= 3
      ? '@max-[485px]/header:flex-col @max-[485px]/header:items-stretch @max-[485px]/header:[&>div]:w-full'
      : '@max-[360px]/header:flex-col @max-[360px]/header:items-stretch @max-[360px]/header:[&>div]:w-full';

  return (
    <div
      className={`ml-auto flex h-7 shrink-0 items-center gap-2 basis-auto @[600px]/header:basis-auto ${
        entry ? '@max-[600px]/header:h-auto @max-[600px]/header:basis-full @max-[600px]/header:ml-0' : ''
      }`}
    >
      <AnimatePresence mode="wait" initial={false}>
        {entry && (
          <ActionsInner
            key={entry.key}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.1, ease: EASE } }}
            transition={{ duration: 0.1, ease: EASE }}
            className={`flex items-center gap-2 flex-wrap justify-end w-full @max-[600px]/header:[&>div]:flex-1 @max-[600px]/header:[&>div]:min-w-0 ${stackClasses}`}
          >
            <StaggeredItems>{entry.content}</StaggeredItems>
          </ActionsInner>
        )}
      </AnimatePresence>
    </div>
  );
};

const StaggeredItems: React.FC<{ children: ReactNode }> = ({ children }) => {
  const items = Children.toArray(children).filter(Boolean);
  const count = items.length;

  return (
    <>
      {items.map((child, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, x: 8, scale: 0.95 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          transition={{ duration: 0.15, ease: EASE, delay: (count - 1 - i) * STAGGER_DELAY }}
        >
          {child}
        </motion.div>
      ))}
    </>
  );
};

let actionKeyCounter = 0;

export const PageActions: React.FC<{ children: ReactNode; headerDecoration?: ReactNode }> = ({
  children,
  headerDecoration,
}) => {
  const setActions = useContext(SetActionsContext);
  const [key] = useState(() => ++actionKeyCounter);

  useEffect(() => {
    setActions?.({ key, content: children, headerDecoration });
    return () => setActions?.(null);
  }, [setActions, key, children, headerDecoration]);

  return null;
};

export const useHeaderBorder = (): HeaderBorderMode => useContext(HeaderBorderContext);

export const PageHeaderBorder: React.FC<{ mode: HeaderBorderMode }> = ({ mode }) => {
  const setHeaderBorder = useContext(SetHeaderBorderContext);

  useEffect(() => {
    setHeaderBorder?.(mode);
    return () => setHeaderBorder?.(true);
  }, [mode, setHeaderBorder]);

  return null;
};
